/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { UserStateService } from 'src/app/shared/services/user-state.service';
import { FilterApiService } from 'src/app/shared/services/customer-api/filter-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { MatLegacySelectionList as MatSelectionList } from '@angular/material/legacy-list';
import { FilterListService } from './service/filter-list.service';
import { MenuFilterParamsService } from './service/menu-filter-params.service';
import { PRESET_NAMES } from './menu-filters-constants';
import { CommonDataParamsService } from 'src/app/shared/utils/common-data-params.service';
import {
  isNavLanguage,
  serializeLanguageParam,
} from 'src/app/shared/utils/language.constants';
import { FilterPresetsListComponent } from './components/filter-presets-list/filter-presets-list.component';

/**
 * main menu filter of the application — orchestrates the per-filter child
 * components and owns the URL queryParams round-trip
 */
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-menu-filters',
  templateUrl: './menu-filters.component.html',
  styleUrls: ['./menu-filters.component.scss'],
})
export class MenuFiltersComponent implements AfterViewInit {
  @ViewChild(FilterPresetsListComponent, { static: true })
  private presetsListComponent: FilterPresetsListComponent;

  /** selection list of the presets child (created with the mat-menu content) */
  private get presetsSelectionList(): MatSelectionList {
    return this.presetsListComponent?.selectionList;
  }

  /**
   * check if filters are activated (non default)
   */
  isFilterActive = false;

  /**
   *
   */
  params: any;

  /**
   * every available language, EN/DE included — the nav-bar toggles are a shortcut
   * view of those two members, not a separate filter
   */
  filterLang = [];

  /**
   * possible labels and values of sentiment filters
   */
  filterSentiment = [
    {
      label: 'Positive',
      value: 'pos',
    },
    {
      label: 'Neutral',
      value: 'neu',
    },
    {
      label: 'Negative',
      value: 'neg',
    },
  ];
  /**
   * form control for sentiment filters
   */
  menuFilterSentiment: UntypedFormControl;

  /**
   * form control for language filters
   */
  menuFilterLang: UntypedFormControl;

  /**
   *
   */
  project: string;

  /**
   * news feed types (PRO/PRINT/WEB/RSS) — no menu UI, but part of the
   * preset/queryParams contract
   */
  filterNews = [
    {
      label: 'PRO',
      value: 'professional',
      icon: 'star',
      active: true,
    },
    {
      label: 'PRINT',
      value: 'print',
      icon: 'list_alt',
      active: true,
    },
    {
      label: 'WEB',
      value: 'online',
      icon: 'dvr',
      active: true,
    },
    {
      label: 'RSS',
      value: 'non_professional',
      icon: 'rss_feed',
      active: true,
    },
  ];

  /**
   * form control of the feed
   */
  menuFilterNews: UntypedFormControl;

  /**
   * document categories disabled by default
   */
  defaultDisabledCategories = [];

  /**
   * filters presets object containing all the data
   */
  presets: any;

  /**
   * default filters preset
   */
  presetDefault = {
    id: 'defaultId',
    name: 'Default',
    feed: 'non_professional,online,print,professional',
    filter: 'pos,neu,neg',
    ranks: null,
    categories: 'glife',
    continents: null,
    countries: null,
    panels: null,
    factuality: null,
    temporality: null,
    event_ids: null,
  };

  /**
   * default filters preset including environmental (E) events
   */
  presetEventsE = {
    id: 'eventsE',
    name: 'E events',
    feed: 'non_professional,online,print,professional',
    filter: null,
    ranks: null,
    categories: 'glife',
    continents: null,
    countries: null,
    panels: null,
    factuality: null,
    temporality: null,
    event_ids:
      'E33_A,E33_B,E33_E,E33_F,E33_J,E33_K,E33_H,E33_I,E49_A,E49_B,E33_D,E34_A,E34_B,E52_B,E52_A,E34_C,E34_D,E33_L',
  };

  /**
   * default filters preset including social (S) events
   */
  presetEventsS = {
    id: 'eventsS',
    name: 'S events',
    feed: 'non_professional,online,print,professional',
    filter: null,
    ranks: null,
    categories: 'glife',
    continents: null,
    countries: null,
    panels: null,
    factuality: null,
    temporality: null,
    event_ids:
      'E48_A,E48_D,E48_E,E48_F,E48_G,E13_B,E13_A,E48_B,E48_C,E51_A,E51_D,E51_B,E3_A,E2_B,E15_A,E2_A,E32_C,E17_B,E17_C,E17_A,E17_D',
  };

  /**
   * default filters preset including governance (G) events
   */
  presetEventsG = {
    id: 'eventsG',
    name: 'G events',
    feed: 'non_professional,online,print,professional',
    filter: null,
    ranks: null,
    categories: 'glife',
    continents: null,
    countries: null,
    panels: null,
    factuality: null,
    temporality: null,
    event_ids:
      'E47_A,E20_A,E22_B,E22_C,E50_B,E50_A,E16_A,E23_A,E33_G,E33_C,E25_E,E21_A,E21_B,E42_C,E45_A,E45_C,E18_B,E18_A,E18_F,E18_G,E6_D,E6_C,E6_B,E6_E,E6_A,E19_A,E19_B',
  };

  /**
   * custom default preset
   */
  presetCustomDefault = {
    id: 'defaultId',
    name: 'Default',
    feed: 'non_professional,online,print,professional',
    filter: 'pos,neu,neg',
    ranks: null,
    categories: 'glife',
    continents: null,
    countries: null,
    panels: null,
    factuality: null,
    temporality: null,
    event_ids: null,
  };

  /**
   * id of the custom default filter
   */
  idCustomDefault: any;

  /**
   * name of the custom default filter
   */
  nameCustomDefault: any;

  /**
   * filters presets array for display
   */
  filterPresets = [];

  /**
   * form control of filters presets
   */
  menuFilterPresets: UntypedFormControl;

  /**
   * all events from URL parameter event_ids
   */
  urlEvents: any;

  /**
   * events object containing info for filtering purposes
   */
  events: any;

  /**
   * events filter
   */
  filterEvents = [];

  /**
   * form control of the events filter
   */
  menuFilterEvents: UntypedFormControl;

  /**
   * sub_events object containing info for filtering purposes
   */
  subEvents: any;

  /**
   * sub_events filter
   */
  filterSubEvents = {};

  /**
   * all subevents
   */
  allSubEvents = [];

  /**
   * document categories filter
   */
  filterCategories = [];

  /**
   * form control of the document categories filter
   */
  menuFilterCategories: UntypedFormControl;

  /**
   * source panels filter
   */
  filterPanels = [];

  /**
   * form control of the source panels filter
   */
  menuFilterPanels: UntypedFormControl;

  /**
   * geography filters group object
   */
  filterGeography = {
    continents: [],
    countries: [],
  };

  /**
   * form control of the geography filters
   */
  menuFilterGeography: UntypedFormControl;

  /**
   * geography group autocompletion form, group and options observable
   */
  geographyForm: UntypedFormGroup;
  geographyGroups = [];
  geographyGroupOptions: Observable<any>;

  /**
   * temporality and factuality filters group object
   */
  filterFactTemp = {
    factuality: [
      {
        value: 'fact',
        label: 'Factual',
        selected: false,
      },
      {
        value: 'probable',
        label: 'Probable',
        selected: false,
      },
      {
        value: 'possible',
        label: 'Possible',
        selected: false,
      },
    ],
    temporality: [
      {
        value: 'past',
        label: 'Past',
        selected: false,
      },
      {
        value: 'present',
        label: 'Present',
        selected: false,
      },
      {
        value: 'future',
        label: 'Future',
        selected: false,
      },
    ],
  };

  /**
   * form control of the factuality/temporality filters
   */
  menuFilterFactTemp: UntypedFormControl;

  /**
   * source ranks filter
   */
  filterRanks = [];

  /**
   * form control of the source ranks filter
   */
  menuFilterRanks: UntypedFormControl;

  /**
   * special tag filter
   */
  tagFilter = null;

  /**
   * special participant filter
   */
  participantFilter = null;

  /**
   * omit resetting filters on menu close
   */
  omitReset = false;

  /**
   * query parameters subscribe and initialize the formcontrols
   */
  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    public featureFlagsService: FeatureFlagsService,
    public userState: UserStateService,
    private filterApi: FilterApiService,
    public config: ConfigService,
    private cd: ChangeDetectorRef,
    private filterListService: FilterListService,
    private filterParams: MenuFilterParamsService,
    private commonDataParams: CommonDataParamsService,
  ) {
    this.project = this.config.appConfig.routing.project;

    this.route.queryParams.subscribe((params) => {
      if (params.tag) {
        this.tagFilter = params.tag;
      } else {
        this.tagFilter = null;
      }
      if (params.participant) {
        this.participantFilter = params.participant;
      } else {
        this.participantFilter = null;
      }

      this.menuFilterPresets = new UntypedFormControl();

      if (this.filterPresets.length === 0) {
        this.filterApi.getFilter().subscribe((result) => {
          this.presets = result;
          this.filterPresets = [];

          const defaultPreset = {
            label: 'Default',
            value: 'Default',
            visible: false,
            presetCopy: JSON.parse(JSON.stringify(this.presetDefault)),
          };

          this.filterPresets.push(defaultPreset);

          if (
            this.featureFlagsService.scorelabSettings.defaultScore === 'esg' ||
            this.featureFlagsService.scorelabSettings.defaultScore ===
              'esg_risk'
          ) {
            const eventsEpreset = {
              label: 'E events',
              value: 'E events',
              visible: false,
              presetCopy: JSON.parse(JSON.stringify(this.presetEventsE)),
            };
            const eventsSpreset = {
              label: 'S events',
              value: 'S events',
              visible: false,
              presetCopy: JSON.parse(JSON.stringify(this.presetEventsS)),
            };
            const eventsGpreset = {
              label: 'G events',
              value: 'G events',
              visible: false,
              presetCopy: JSON.parse(JSON.stringify(this.presetEventsG)),
            };
            this.filterPresets.push(eventsEpreset);
            this.filterPresets.push(eventsSpreset);
            this.filterPresets.push(eventsGpreset);
          }

          result.forEach((item) => {
            if (item.name === '_default_') {
              Object.assign(this.presetCustomDefault, item);
              this.idCustomDefault = item.id;
              if (item.defaultFilter) {
                this.nameCustomDefault = item.defaultFilter;
              }
            } else {
              this.filterPresets.push({
                label: item.name,
                value: item.name,
                visible: false,
                presetCopy: item,
              });
            }
          });

          // Move custom default filter to the very top of the list
          if (this.nameCustomDefault) {
            const customDefaultIndex = this.filterPresets.findIndex(
              (preset) => preset.label === this.nameCustomDefault,
            );
            if (customDefaultIndex > 0) {
              const customDefaultPreset = this.filterPresets.splice(
                customDefaultIndex,
                1,
              )[0];
              this.filterPresets.unshift(customDefaultPreset);
            }
          }

          if (
            this.filterParams.hasFilterChanged(params, this.presetCustomDefault)
          ) {
            this.isFilterActive = true;
            if (this.userState.filter && this.userState.filter === 'Default') {
              this.userState.filter = 'Custom';
            }

            let presetId;
            // @ts-expect-error Description of why the error is expected
            this.presetsSelectionList.options._results.forEach(
              (item, index) => {
                if (item._value === this.userState.filter) {
                  presetId = index;
                  setTimeout(() => {
                    if (
                      this.presetsSelectionList &&
                      // @ts-expect-error Description of why the error is expected
                      this.presetsSelectionList.options._results
                    ) {
                      this.presetsSelectionList.deselectAll();
                      // @ts-expect-error Description of why the error is expected
                      this.presetsSelectionList.options._results[
                        presetId
                      ]._setSelected(true);
                    }
                  }, 200);
                }
              },
            );
          } else {
            this.isFilterActive = false;
            // Only set default if no filter is currently selected
            if (!this.userState.filter) {
              this.userState.filter = this.nameCustomDefault || 'Default';
            }

            setTimeout(() => {
              if (
                this.presetsSelectionList &&
                this.presetsSelectionList.options
              ) {
                // Find and select the current filter
                // @ts-expect-error Accessing private _results property
                const results = this.presetsSelectionList.options._results;
                const currentFilterOption = results.find(
                  (option: any) => option._value === this.userState.filter,
                );

                if (currentFilterOption) {
                  this.presetsSelectionList.deselectAll();
                  currentFilterOption._setSelected(true);
                } else {
                  // Fallback: select custom default or first option
                  this.presetsSelectionList.deselectAll();
                  let presetToSelect = null;
                  if (this.nameCustomDefault) {
                    presetToSelect = results.find(
                      (option: any) => option._value === this.nameCustomDefault,
                    );
                  }
                  if (
                    !presetToSelect &&
                    this.presetsSelectionList.options.first
                  ) {
                    presetToSelect = this.presetsSelectionList.options.first;
                  }
                  if (presetToSelect) {
                    presetToSelect._setSelected(true);
                  }
                }
              }
            }, 200);
          }
        });
      } else {
        // Check if E events already exists in the array
        const hasEEvents = this.filterPresets.some(
          (preset) => preset.value === 'E events',
        );

        if (
          (this.featureFlagsService.scorelabSettings.defaultScore === 'esg' ||
            this.featureFlagsService.scorelabSettings.defaultScore ===
              'esg_risk') &&
          this.filterPresets &&
          this.filterPresets.length > 0 &&
          !hasEEvents
        ) {
          const eventsEpreset = {
            label: 'E events',
            value: 'E events',
            visible: false,
            presetCopy: JSON.parse(JSON.stringify(this.presetEventsE)),
          };
          const eventsSpreset = {
            label: 'S events',
            value: 'S events',
            visible: false,
            presetCopy: JSON.parse(JSON.stringify(this.presetEventsS)),
          };
          const eventsGpreset = {
            label: 'G events',
            value: 'G events',
            visible: false,
            presetCopy: JSON.parse(JSON.stringify(this.presetEventsG)),
          };
          // Insert E, S, G events after the Default preset
          const insertIndex = this.nameCustomDefault ? 2 : 1;
          this.filterPresets.splice(
            insertIndex,
            0,
            eventsEpreset,
            eventsSpreset,
            eventsGpreset,
          );
        }

        if (
          this.filterParams.hasFilterChanged(params, this.presetCustomDefault)
        ) {
          this.isFilterActive = true;
          if (this.userState.filter && this.userState.filter === 'Default') {
            this.userState.filter = 'Custom';
          }
          let presetId;
          // @ts-expect-error Description of why the error is expected
          this.presetsSelectionList.options._results.forEach((item, index) => {
            if (item._value === this.userState.filter) {
              presetId = index;
              setTimeout(() => {
                if (
                  this.presetsSelectionList &&
                  // @ts-expect-error Description of why the error is expected
                  this.presetsSelectionList.options._results
                ) {
                  this.presetsSelectionList.deselectAll();
                  // @ts-expect-error Description of why the error is expected
                  this.presetsSelectionList.options._results[
                    presetId
                  ]._setSelected(true);
                }
              }, 200);
            }
          });
        } else {
          this.isFilterActive = false;
          // Only set default if no filter is currently selected
          if (!this.userState.filter) {
            this.userState.filter = this.nameCustomDefault || 'Default';
          }
          setTimeout(() => {
            if (
              this.presetsSelectionList &&
              this.presetsSelectionList.options
            ) {
              // Find and select the current filter
              // @ts-expect-error Accessing private _results property
              const results = this.presetsSelectionList.options._results;
              const currentFilterOption = results.find(
                (option: any) => option._value === this.userState.filter,
              );

              if (currentFilterOption) {
                this.presetsSelectionList.deselectAll();
                currentFilterOption._setSelected(true);
              } else {
                // Fallback: select custom default or first option
                this.presetsSelectionList.deselectAll();
                let presetToSelect = null;
                if (this.nameCustomDefault) {
                  presetToSelect = results.find(
                    (option: any) => option._value === this.nameCustomDefault,
                  );
                }
                if (
                  !presetToSelect &&
                  this.presetsSelectionList.options.first
                ) {
                  presetToSelect = this.presetsSelectionList.options.first;
                }
                if (presetToSelect) {
                  presetToSelect._setSelected(true);
                }
              }
            }
          }, 200);
        }
      }

      if (params.eventid && params.eventype === 'sub_event') {
        this.urlEvents = params.eventid.split(',');
        this.menuFilterEvents = new UntypedFormControl(
          params.eventid.split(','),
        );
      } else if (params.event_ids) {
        this.urlEvents = params.event_ids.split(',');
        this.menuFilterEvents = new UntypedFormControl(
          params.event_ids.split(','),
        );
      } else {
        this.urlEvents = [];
        this.menuFilterEvents = new UntypedFormControl();
      }
      this.events = [];
      this.subEvents = [];

      if (params.categories) {
        this.menuFilterCategories = new UntypedFormControl(
          this.filterCategories
            .filter(
              (option) => !params.categories.split(',').includes(option.value),
            )
            .map((option) => option.value),
        );
      } else {
        this.menuFilterCategories = new UntypedFormControl(
          this.filterCategories.map((option) => option.value),
        );
      }
      if (params.ranks) {
        this.menuFilterRanks = new UntypedFormControl(params.ranks.split(','));
      } else {
        this.menuFilterRanks = new UntypedFormControl();
      }
      if (params.panels) {
        this.menuFilterPanels = new UntypedFormControl(
          params.panels.split(','),
        );
      } else {
        this.menuFilterPanels = new UntypedFormControl();
      }
      let geography = [];
      if (params.continents) {
        const continentsArray = params.continents
          .split(',')
          .map((continent) => 'continent ' + continent);
        geography = geography.concat(continentsArray);
      }
      if (params.countries) {
        const countriesArray = params.countries
          .split(',')
          .map((country) => 'country ' + country);
        geography = geography.concat(countriesArray);
      }
      this.menuFilterGeography = new UntypedFormControl(geography);

      this.geographyForm = this.formBuilder.group({
        geographyGroup: '',
      });

      this.geographyGroupOptions = this.geographyForm
        .get('geographyGroup')!
        .valueChanges.pipe(
          startWith(''),
          map((value) => this.groupMatches(value)),
        );

      let tempAndFact = [];
      if (params.factuality) {
        const factualityArray = params.factuality.split(',');
        tempAndFact = tempAndFact.concat(factualityArray);
      }
      if (params.temporality) {
        const temporalityArray = params.temporality.split(',');
        tempAndFact = tempAndFact.concat(temporalityArray);
      }
      this.menuFilterFactTemp = new UntypedFormControl(tempAndFact);

      if (
        this.filterLang.length === 0 ||
        this.filterCategories.length === 0 ||
        this.filterRanks.length === 0 ||
        this.filterGeography.continents.length === 0 ||
        this.filterGeography.countries.length === 0 ||
        this.filterPanels.length === 0 ||
        this.filterEvents.length === 0
      ) {
        this.filterListService.getFilterListItem().subscribe((result) => {
          const languages = result.languages;
          const documentCategories = result.documentCategories;
          const sourceRanks = result.sourceRanks;
          const sourceContinents = result.sourceContinents;
          const sourceCountries = result.sourceCountries;
          const sourcePanels = result.sourcePanels;

          const events = result.events;
          if (events.length === 1) {
            this.filterEvents = result.filterEvents;
            this.filterSubEvents = result.filterSubEvents;
            this.allSubEvents = result.allSubEvents;

            this.filterEvents.forEach((element, index) => {
              if (
                this.urlEvents.length !== 0 &&
                this.urlEvents.includes(element.value)
              ) {
                this.filterEvents[index].selected = true;
                this.events.push(element.value);
              } else {
                this.filterEvents[index].selected = false;
              }
              this.filterSubEvents[element.value].forEach(
                (element2, index2) => {
                  if (
                    this.urlEvents.length !== 0 &&
                    this.urlEvents.includes(element2.value)
                  ) {
                    this.filterSubEvents[element.value][index2].selected = true;
                    this.subEvents.push(element2.value);
                  } else {
                    this.filterSubEvents[element.value][index2].selected =
                      false;
                  }
                },
              );
            });
          }
          if (documentCategories.length === 1) {
            // Parents and their children are independent exclusion codes, so
            // they are listed flat in API order.
            this.filterCategories = documentCategories[0].values
              .flatMap((element) => [element, ...(element.children ?? [])])
              .map((element) => {
                return {
                  value: element.value,
                  label: element.name,
                  selected: !this.defaultDisabledCategories.includes(
                    element.value,
                  ),
                };
              });
            if (params.categories) {
              this.menuFilterCategories = new UntypedFormControl(
                this.filterCategories
                  .filter(
                    (option) =>
                      !params.categories.split(',').includes(option.value),
                  )
                  .map((option) => option.value),
              );
            } else {
              this.menuFilterCategories = new UntypedFormControl(
                this.filterCategories.map((option) => option.value),
              );
            }
            this.filterCategories.forEach((element, index) => {
              if (this.menuFilterCategories.value !== null) {
                this.filterCategories[index].selected =
                  this.menuFilterCategories.value.includes(element.value);
              } else {
                this.filterCategories[index].selected =
                  !this.defaultDisabledCategories.includes(element.value);
              }
            });
          } else {
            this.filterCategories = [];
          }
          if (languages.length === 1) {
            // EN/DE always listed; the rest only with betaLanguages.
            this.filterLang = languages[0].values
              .filter(
                (element) =>
                  this.featureFlagsService.featureFlags.betaLanguages ||
                  isNavLanguage(element.value),
              )
              .map((element) => ({
                value: element.value,
                label: element.name,
              }));
            this.menuFilterLang = new UntypedFormControl(
              this.commonDataParams.getLanguageFilter(params),
            );
          } else {
            this.filterLang = [];
          }
          if (sourceRanks.length === 1) {
            this.filterRanks = sourceRanks[0].values.map((element) => {
              return {
                value: element.value.toString(),
                label: 'Rank ' + element.name,
              };
            });
          } else {
            this.filterRanks = [];
          }
          if (sourceContinents.length === 1) {
            this.filterGeography.continents = sourceContinents[0].values.map(
              (element) => {
                return {
                  value: 'continent ' + element.value,
                  label: element.name,
                  selected: false,
                };
              },
            );
            this.filterGeography.continents.forEach((element, index) => {
              if (this.menuFilterGeography.value !== null) {
                this.filterGeography.continents[index].selected =
                  this.menuFilterGeography.value.includes(element.value);
              } else {
                this.filterGeography.continents[index].selected = false;
              }
            });
          } else {
            this.filterGeography.continents = [];
          }
          if (sourceCountries.length === 1) {
            this.filterGeography.countries = sourceCountries[0].values.map(
              (element) => {
                return {
                  value: 'country ' + element.value,
                  label: element.name,
                  selected: false,
                };
              },
            );
            this.filterGeography.countries.forEach((element, index) => {
              if (this.menuFilterGeography.value !== null) {
                this.filterGeography.countries[index].selected =
                  this.menuFilterGeography.value.includes(element.value);
              } else {
                this.filterGeography.countries[index].selected = false;
              }
            });
          } else {
            this.filterGeography.countries = [];
          }
          if (sourcePanels.length === 1) {
            this.filterPanels = sourcePanels[0].values.map((element) => {
              return {
                value: element.value.toString(),
                label: element.name,
              };
            });
          } else {
            this.filterPanels = [];
          }

          this.geographyGroups = [
            {
              type: 'Continents',
              names: this.filterGeography.continents.map(
                (continent) => continent.label,
              ),
            },
            {
              type: 'Countries',
              names: this.filterGeography.countries.map(
                (country) => country.label,
              ),
            },
          ];
        });
      } else {
        this.filterCategories.forEach((element, index) => {
          if (this.menuFilterCategories.value !== null) {
            this.filterCategories[index].selected =
              this.menuFilterCategories.value.includes(element.value);
          } else {
            this.filterCategories[index].selected =
              !this.defaultDisabledCategories.includes(element.value);
          }
        });
        this.filterGeography.continents.forEach((element, index) => {
          if (this.menuFilterGeography.value !== null) {
            this.filterGeography.continents[index].selected =
              this.menuFilterGeography.value.includes(element.value);
          } else {
            this.filterGeography.continents[index].selected = false;
          }
        });
        this.filterGeography.countries.forEach((element, index) => {
          if (this.menuFilterGeography.value !== null) {
            this.filterGeography.countries[index].selected =
              this.menuFilterGeography.value.includes(element.value);
          } else {
            this.filterGeography.countries[index].selected = false;
          }
        });
        this.filterEvents.forEach((element, index) => {
          if (
            this.urlEvents.length !== 0 &&
            this.urlEvents.includes(element.value)
          ) {
            this.filterEvents[index].selected = true;
            this.events.push(element.value);
          } else {
            this.filterEvents[index].selected = false;
          }
          this.filterSubEvents[element.value].forEach((element2, index2) => {
            if (
              this.urlEvents.length !== 0 &&
              this.urlEvents.includes(element2.value)
            ) {
              this.filterSubEvents[element.value][index2].selected = true;
              this.subEvents.push(element2.value);
            } else {
              this.filterSubEvents[element.value][index2].selected = false;
            }
          });
        });
        this.filterFactTemp.factuality.forEach((element, index) => {
          if (this.menuFilterFactTemp.value !== null) {
            this.filterFactTemp.factuality[index].selected =
              this.menuFilterFactTemp.value.includes(element.value);
          } else {
            this.filterFactTemp.factuality[index].selected = false;
          }
        });
        this.filterFactTemp.temporality.forEach((element, index) => {
          if (this.menuFilterFactTemp.value !== null) {
            this.filterFactTemp.temporality[index].selected =
              this.menuFilterFactTemp.value.includes(element.value);
          } else {
            this.filterFactTemp.temporality[index].selected = false;
          }
        });
      }

      if (params.filter) {
        this.menuFilterSentiment = new UntypedFormControl(
          params.filter.split(','),
        );
      } else {
        this.menuFilterSentiment = new UntypedFormControl(
          this.filterSentiment.map((option) => option.value),
        );
      }
      this.menuFilterLang = new UntypedFormControl(
        this.commonDataParams.getLanguageFilter(params),
      );
      if (params.feed) {
        this.menuFilterNews = new UntypedFormControl(params.feed.split(','));
      } else {
        this.menuFilterNews = new UntypedFormControl(
          this.filterNews.map((option) => {
            if (option.active) {
              return option.value;
            }
          }),
        );
      }
      this.params = params;
    });
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  /**
   * open dialog datepicher
   */
  handleSearch(name, type) {
    const geographyArray = this.menuFilterGeography.value;
    if (type === 'Continents') {
      this.filterGeography.continents.forEach((element, index) => {
        if (element.label === name && element.selected === false) {
          this.filterGeography.continents[index].selected = true;
          geographyArray.push(element.value);
          this.menuFilterGeography.setValue(geographyArray);
        }
      });
    } else if (type === 'Countries') {
      this.filterGeography.countries.forEach((element, index) => {
        if (element.label === name && element.selected === false) {
          this.filterGeography.countries[index].selected = true;
          geographyArray.push(element.value);
          this.menuFilterGeography.setValue(geographyArray);
        }
      });
    }
  }

  /**
   * flag selected items from multi-selection lists
   */
  handleSelection(element) {
    element.selected = !element.selected;

    // Update form controls to keep them in sync with selected state
    // For categories
    if (this.filterCategories && this.filterCategories.includes(element)) {
      const selectedCategories = this.filterCategories
        .filter((cat) => cat.selected)
        .map((cat) => cat.value);
      this.menuFilterCategories.setValue(selectedCategories);
    }

    // For events/subevents
    let isSubevent = false;
    if (this.filterSubEvents) {
      for (const eventKey in this.filterSubEvents) {
        if (this.filterSubEvents[eventKey].includes(element)) {
          isSubevent = true;
          break;
        }
      }
    }

    if (isSubevent) {
      const selectedEvents = [];
      this.filterEvents.forEach((event) => {
        this.filterSubEvents[event.value].forEach((subevent) => {
          if (subevent.selected) {
            selectedEvents.push(subevent.value);
          }
        });
      });
      this.menuFilterEvents.setValue(selectedEvents);
    }

    this.presetsSelectionList.deselectAll();
    this.userState.filter = 'Custom';
  }

  /**
   * select or deselect all events at once
   */
  toggleAllEvents() {
    const events = [];
    this.filterEvents.forEach((event) => {
      this.filterSubEvents[event.value].forEach((subevent) => {
        events.push(subevent.value);
      });
    });
    const shouldSelectAll =
      this.menuFilterEvents.value?.length !== events.length;
    if (shouldSelectAll) {
      this.filterEvents.forEach((event) => {
        this.filterSubEvents[event.value].forEach((subevent) => {
          subevent.selected = true;
        });
      });
      this.menuFilterEvents.setValue(events);
    } else {
      this.filterEvents.forEach((event) => {
        this.filterSubEvents[event.value].forEach((subevent) => {
          subevent.selected = false;
        });
      });
      this.menuFilterEvents.setValue([]);
    }
    if (
      this.menuFilterEvents.value &&
      (this.menuFilterEvents.value.length === events.length ||
        this.menuFilterEvents.value.length === 0)
    ) {
      this.markFilterAsCustom();
    }
  }

  /**
   * select or deselect all categories at once
   */
  toggleAllCategories() {
    this.toggleAllItems(this.filterCategories, this.menuFilterCategories);
  }

  /**
   * deselect selected preset on filters change
   */
  handleNoSelection() {
    this.presetsSelectionList.deselectAll();
    this.userState.filter = 'Custom';
  }

  /**
   * apply selected preset to all filters
   */
  handlePresetSelection(selectedPreset) {
    this.userState.filter = selectedPreset.label;

    if (selectedPreset.presetCopy.filter) {
      this.menuFilterSentiment = new UntypedFormControl(
        selectedPreset.presetCopy.filter.split(','),
      );
    } else {
      this.menuFilterSentiment = new UntypedFormControl(
        this.filterSentiment.map((option) => option.value),
      );
    }

    this.menuFilterLang = new UntypedFormControl(
      this.commonDataParams.getLanguageFilter({
        lang: selectedPreset.presetCopy.lang,
      }),
    );

    if (selectedPreset.presetCopy.ranks) {
      this.menuFilterRanks = new UntypedFormControl(
        selectedPreset.presetCopy.ranks.split(','),
      );
    } else {
      this.menuFilterRanks = new UntypedFormControl();
    }

    if (selectedPreset.presetCopy.panels) {
      this.menuFilterPanels = new UntypedFormControl(
        selectedPreset.presetCopy.panels.split(','),
      );
    } else {
      this.menuFilterPanels = new UntypedFormControl();
    }

    if (selectedPreset.presetCopy.categories) {
      this.menuFilterCategories = new UntypedFormControl(
        this.filterCategories
          .filter(
            (option) =>
              !selectedPreset.presetCopy.categories
                .split(',')
                .includes(option.value),
          )
          .map((option) => option.value),
      );
    } else {
      this.menuFilterCategories = new UntypedFormControl(
        this.filterCategories
          .filter(
            (option) => !this.defaultDisabledCategories.includes(option.value),
          )
          .map((option) => option.value),
      );
    }
    this.filterCategories.forEach((element, index) => {
      if (this.menuFilterCategories.value !== null) {
        this.filterCategories[index].selected =
          this.menuFilterCategories.value.includes(element.value);
      } else {
        this.filterCategories[index].selected =
          !this.defaultDisabledCategories.includes(element.value);
      }
    });

    let geography = [];
    if (selectedPreset.presetCopy.continents) {
      const continentsArray = selectedPreset.presetCopy.continents
        .split(',')
        .map((continent) => 'continent ' + continent);
      geography = geography.concat(continentsArray);
    }
    if (selectedPreset.presetCopy.countries) {
      const countriesArray = selectedPreset.presetCopy.countries
        .split(',')
        .map((country) => 'country ' + country);
      geography = geography.concat(countriesArray);
    }
    this.menuFilterGeography = new UntypedFormControl(geography);
    this.filterGeography.continents.forEach((element, index) => {
      if (this.menuFilterGeography.value !== null) {
        this.filterGeography.continents[index].selected =
          this.menuFilterGeography.value.includes(element.value);
      } else {
        this.filterGeography.continents[index].selected = false;
      }
    });
    this.filterGeography.countries.forEach((element, index) => {
      if (this.menuFilterGeography.value !== null) {
        this.filterGeography.countries[index].selected =
          this.menuFilterGeography.value.includes(element.value);
      } else {
        this.filterGeography.countries[index].selected = false;
      }
    });

    let facttemp = [];
    if (selectedPreset.presetCopy.factuality) {
      const factualityArray = selectedPreset.presetCopy.factuality.split(',');
      facttemp = facttemp.concat(factualityArray);
    }
    if (selectedPreset.presetCopy.temporality) {
      const temporalityArray = selectedPreset.presetCopy.temporality.split(',');
      facttemp = facttemp.concat(temporalityArray);
    }
    this.menuFilterFactTemp = new UntypedFormControl(facttemp);
    this.filterFactTemp.factuality.forEach((element, index) => {
      if (this.menuFilterFactTemp.value !== null) {
        this.filterFactTemp.factuality[index].selected =
          this.menuFilterFactTemp.value.includes(element.value);
      } else {
        this.filterFactTemp.factuality[index].selected = false;
      }
    });
    this.filterFactTemp.temporality.forEach((element, index) => {
      if (this.menuFilterFactTemp.value !== null) {
        this.filterFactTemp.temporality[index].selected =
          this.menuFilterFactTemp.value.includes(element.value);
      } else {
        this.filterFactTemp.temporality[index].selected = false;
      }
    });

    if (selectedPreset.presetCopy.event_ids) {
      this.urlEvents = selectedPreset.presetCopy.event_ids.split(',');
      this.menuFilterEvents = new UntypedFormControl(
        selectedPreset.presetCopy.event_ids.split(','),
      );
    } else {
      this.urlEvents = [];
      this.menuFilterEvents = new UntypedFormControl();
    }
    this.events = [];
    this.subEvents = [];
    this.filterEvents.forEach((element, index) => {
      if (
        this.urlEvents.length !== 0 &&
        this.urlEvents.includes(element.value)
      ) {
        this.filterEvents[index].selected = true;
        this.events.push(element.value);
      } else {
        this.filterEvents[index].selected = false;
      }
      this.filterSubEvents[element.value].forEach((element2, index2) => {
        if (
          this.urlEvents.length !== 0 &&
          this.urlEvents.includes(element2.value)
        ) {
          this.filterSubEvents[element.value][index2].selected = true;
          this.subEvents.push(element2.value);
        } else {
          this.filterSubEvents[element.value][index2].selected = false;
        }
      });
    });

    if (selectedPreset.presetCopy.feed) {
      this.menuFilterNews = new UntypedFormControl(
        selectedPreset.presetCopy.feed.split(','),
      );
    } else {
      this.menuFilterNews = new UntypedFormControl(
        this.filterNews.map((option) => {
          if (option.active) {
            return option.value;
          }
        }),
      );
    }
  }

  /**
   * make single selection work for 'mat-selection-list'
   * @param event
   * @param type
   */
  handleSingleSelection(event, type) {
    if (event.options[0].selected) {
      event.source.deselectAll();
      event.options[0]._setSelected(true);
      if (type === 'preset') {
        const selectedPreset = this.filterPresets.filter((preset) => {
          return preset.value === event.options[0].value;
        })[0];
        this.handlePresetSelection(selectedPreset);
      }
    } else {
      if (type === 'preset') {
        event.source.deselectAll();
        event.options[0]._setSelected(true);
      }
    }
  }

  /**
   * returns matching items
   */
  elementMatches(options, value) {
    const filterValue = value.toLowerCase();
    return options.filter(
      (item) => item.toLowerCase().indexOf(filterValue) === 0,
    );
  }

  /**
   * returns matching group's items
   */
  groupMatches(value) {
    if (value) {
      return this.geographyGroups
        .map((group) => ({
          type: group.type,
          names: this.elementMatches(group.names, value),
        }))
        .filter((group) => group.names.length > 0);
    }
    return this.geographyGroups;
  }

  /**
   * clear all filters if not applied on close
   */
  resetFilterOptions() {
    if (!this.omitReset) {
      this.router.navigate([], {
        queryParams: {
          resetFilters: Math.random(),
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    this.omitReset = false;
  }

  /**
   * setup all the filtering options from the main filter menu
   */
  setFilterOptions() {
    this.omitReset = true;

    // Process all filter values. Timeframe is deliberately absent: it belongs to
    // the persistent nav-bar control and must survive an Apply untouched.
    const sentiment = this.processSentimentFilter();
    const lang = this.processLanguageFilter();
    const feed = this.processFeedFilter();
    const events = this.filterParams.joinSelection(this.menuFilterEvents.value);
    const categories = this.filterParams.unselectedValues(
      this.filterCategories,
      this.menuFilterCategories.value,
    );
    const { continents, countries } = this.processGeographyFilters();
    const factuality = this.filterParams.subsetParam(
      this.menuFilterFactTemp.value,
      this.filterFactTemp.factuality.map((item) => item.value),
    );
    const temporality = this.filterParams.subsetParam(
      this.menuFilterFactTemp.value,
      this.filterFactTemp.temporality.map((item) => item.value),
    );
    const ranks = this.filterParams.joinSelection(this.menuFilterRanks.value);
    const panels = this.filterParams.joinSelection(this.menuFilterPanels.value);

    // Check if event should be unselected
    const checkIfEventUnselected =
      this.params.eventid &&
      this.menuFilterEvents.value.length === 1 &&
      this.menuFilterEvents.value.includes(this.params.eventid)
        ? false
        : true;

    this.router.navigate([], {
      queryParams: {
        filter: sentiment,
        lang: lang,
        feed: feed,
        event_ids: checkIfEventUnselected
          ? events
          : this.params.event_ids
            ? this.params.event_ids
            : null,
        eventid: checkIfEventUnselected
          ? null
          : this.params.eventid
            ? this.params.eventid
            : null,
        eventype: checkIfEventUnselected
          ? null
          : this.params.eventype
            ? this.params.eventype
            : null,
        factuality: factuality,
        temporality: temporality,
        categories: categories,
        continents: continents,
        countries: countries,
        panels: panels,
        ranks: ranks,
        tag: this.tagFilter,
        participant: this.participantFilter,
        newsday: null,
        // score: null
      },
      queryParamsHandling: 'merge' /*, replaceUrl: true*/,
    });
  }

  /**
   * save a preset filter
   */
  savePreset() {
    // Timeframe is not part of a preset — it is an independent nav-bar control.
    const sentiment = this.processSentimentFilter();
    const lang = this.processLanguageFilter();
    const feed = this.processFeedFilter();
    const events = this.filterParams.joinSelection(this.menuFilterEvents.value);
    const categories = this.filterParams.unselectedValues(
      this.filterCategories,
      this.menuFilterCategories.value,
    );
    const { continents, countries } = this.processGeographyFilters();
    const factuality = this.filterParams.subsetParam(
      this.menuFilterFactTemp.value,
      this.filterFactTemp.factuality.map((item) => item.value),
    );
    const temporality = this.filterParams.subsetParam(
      this.menuFilterFactTemp.value,
      this.filterFactTemp.temporality.map((item) => item.value),
    );
    const ranks = this.filterParams.joinSelection(this.menuFilterRanks.value);
    const panels = this.filterParams.joinSelection(this.menuFilterPanels.value);

    const forbiddenNames = [
      'Default',
      '_default_',
      'E events',
      'S events',
      'G events',
    ];
    const existingNames = this.filterPresets.map((preset) => {
      return preset.label;
    });

    const name = prompt('Name of the filter:', '');
    if (
      name &&
      name !== '' &&
      !forbiddenNames.includes(name) &&
      !existingNames.includes(name)
    ) {
      const filter = {
        name: name,
        filter: sentiment,
        lang: lang,
        feed: feed,
        event_ids: events,
        factuality: factuality,
        temporality: temporality,
        categories: categories,
        continents: continents,
        countries: countries,
        panels: panels,
        ranks: ranks,
      };
      this.filterPresets.splice(1, 0, {
        value: name,
        label: name,
        visible: false,
        presetCopy: filter,
      });
      this.filterApi.setFilter(filter).subscribe(() => {
        this.presetsSelectionList.deselectAll();
        // @ts-expect-error Description: This error is expected because the following code is accessing a private property.
        this.presetsSelectionList.options._results[1]._setSelected(true);
      });
    } else if (forbiddenNames.includes(name)) {
      alert(
        'Provided name is reserved for special filter presets. Please choose a different name.',
      );
    } else if (existingNames.includes(name)) {
      alert(
        'Provided name belongs to one of the existing filter presets. Please choose a different name.',
      );
    }
  }

  /**
   * delete a preset filter
   */
  delPreset(filter) {
    if (window.confirm('Delete ' + filter.value + ' filter?')) {
      this.filterPresets = this.filterPresets.filter((item) => {
        return item.value !== filter.value;
      });
      if (
        this.menuFilterPresets.value &&
        this.menuFilterPresets.value.includes(filter.value)
      ) {
        this.menuFilterPresets = new UntypedFormControl();
      }
      this.filterApi.getFilter().subscribe((result) => {
        const filterId = result.filter((item) => {
          return item.name === filter.value;
        })[0].id;
        this.filterApi.delFilter(filterId).subscribe();
      });
    }
  }

  /**
   * reorder filter presets to put custom default or Default at the top
   */
  reorderFilterPresets() {
    if (this.nameCustomDefault) {
      // Move custom default filter to the very top of the list
      const customDefaultIndex = this.filterPresets.findIndex(
        (preset) => preset.label === this.nameCustomDefault,
      );
      if (customDefaultIndex > 0) {
        const customDefaultPreset = this.filterPresets.splice(
          customDefaultIndex,
          1,
        )[0];
        this.filterPresets.unshift(customDefaultPreset);
      }
    } else {
      // Move Default preset back to the top
      const defaultIndex = this.filterPresets.findIndex(
        (preset) => preset.label === 'Default',
      );
      if (defaultIndex > 0) {
        const defaultPreset = this.filterPresets.splice(defaultIndex, 1)[0];
        this.filterPresets.unshift(defaultPreset);
      }
    }
  }

  /**
   * set chosen preset as a default filter
   */
  setDefaultPreset(filter) {
    if (window.confirm('Set ' + filter.value + ' as a default filter?')) {
      const defaultFilter = {};
      Object.assign(
        defaultFilter,
        {
          feed: filter.presetCopy.feed,
          filter: filter.presetCopy.filter,
          lang: filter.presetCopy.lang,
          ranks: filter.presetCopy.ranks,
          panels: filter.presetCopy.panels,
          categories: filter.presetCopy.categories,
          continents: filter.presetCopy.continents,
          countries: filter.presetCopy.countries,
          factuality: filter.presetCopy.factuality,
          temporality: filter.presetCopy.temporality,
          event_ids: filter.presetCopy.event_ids,
        },
        {
          name: '_default_',
          defaultFilter: filter.label,
        },
      );
      if (this.idCustomDefault !== undefined) {
        this.filterApi.delFilter(this.idCustomDefault).subscribe(() => {
          this.filterApi.setFilter(defaultFilter).subscribe((result) => {
            Object.assign(this.presetCustomDefault, defaultFilter);
            this.idCustomDefault = result.id;
            this.nameCustomDefault = filter.label;
            this.userState.filter = filter.label;
            this.reorderFilterPresets();
            this.presetsSelectionList.deselectAll();
            // Find and select the custom default filter
            // @ts-expect-error Accessing private _results property
            const results = this.presetsSelectionList.options._results;
            const customDefaultOption = results.find(
              (option: any) => option._value === filter.label,
            );
            if (customDefaultOption) {
              customDefaultOption._setSelected(true);
            }
            this.handlePresetSelection({
              value: filter.value,
              label: filter.label,
              visible: false,
              presetCopy: filter.presetCopy,
            });
          });
        });
      } else {
        this.filterApi.setFilter(defaultFilter).subscribe((result) => {
          Object.assign(this.presetCustomDefault, defaultFilter);
          this.idCustomDefault = result.id;
          this.nameCustomDefault = filter.label;
          this.userState.filter = filter.label;
          this.reorderFilterPresets();
          this.presetsSelectionList.deselectAll();
          // Find and select the custom default filter
          // @ts-expect-error Accessing private _results property
          const results = this.presetsSelectionList.options._results;
          const customDefaultOption = results.find(
            (option: any) => option._value === filter.label,
          );
          if (customDefaultOption) {
            customDefaultOption._setSelected(true);
          }
          this.handlePresetSelection({
            value: filter.value,
            label: filter.label,
            visible: false,
            presetCopy: filter.presetCopy,
          });
        });
      }
    }
  }

  /**
   * reset default filter back to the original Default preset
   */
  resetDefaultPreset() {
    if (window.confirm('Reset default filter?')) {
      if (this.idCustomDefault !== undefined) {
        this.filterApi.delFilter(this.idCustomDefault).subscribe();
        Object.assign(this.presetCustomDefault, this.presetDefault);
        this.idCustomDefault = undefined;
        this.nameCustomDefault = undefined;
        this.userState.filter = 'Default';
        this.reorderFilterPresets();
        this.presetsSelectionList.deselectAll();
        // Find and select the Default preset
        // @ts-expect-error Accessing private _results property
        const results = this.presetsSelectionList.options._results;
        const defaultOption = results.find(
          (option: any) => option._value === 'Default',
        );
        if (defaultOption) {
          defaultOption._setSelected(true);
        }
        this.handlePresetSelection({
          value: 'Default',
          label: 'Default',
          visible: false,
          presetCopy: this.presetDefault,
        });
      }
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Mark the current filter as custom
   */
  private markFilterAsCustom(): void {
    this.presetsSelectionList.deselectAll();
    this.userState.filter = PRESET_NAMES.CUSTOM;
  }

  /**
   * Process sentiment filter values (resets control to all when emptied)
   */
  private processSentimentFilter(): string | null {
    const sentiment = this.filterParams.joinUnlessAll(
      this.menuFilterSentiment.value,
      this.filterSentiment.length,
    );
    if (this.menuFilterSentiment.value.length === 0) {
      this.menuFilterSentiment = new UntypedFormControl(
        this.filterSentiment.map((option) => option.value),
      );
    }
    return sentiment;
  }

  /**
   * Process language filter values (resets control to all when emptied)
   */
  /**
   * This column lists every language, EN/DE included, so its selection is the whole
   * `lang` value — the nav-bar toggles are a shortcut view of the same two members.
   */
  private processLanguageFilter(): string | null {
    return serializeLanguageParam(this.menuFilterLang.value ?? []);
  }

  /**
   * Process feed filter values (resets control to all when emptied)
   */
  private processFeedFilter(): string | null {
    const feed = this.filterParams.joinUnlessAll(
      this.menuFilterNews.value,
      this.filterNews.length,
    );
    if (this.menuFilterNews.value.length === 0) {
      this.menuFilterNews = new UntypedFormControl(
        this.filterNews.map((option) => option.value),
      );
    }
    return feed;
  }

  /**
   * Process geography filters (continents and countries)
   */
  private processGeographyFilters(): {
    continents: string | null;
    countries: string | null;
  } {
    return this.filterParams.geographyParams(
      this.menuFilterGeography.value,
      this.filterGeography.continents.map((item) => item.value),
      this.filterGeography.countries.map((item) => item.value),
    );
  }

  /**
   * Toggle all items in a list
   */
  private toggleAllItems<T extends { selected: boolean; value: string }>(
    items: T[],
    formControl: UntypedFormControl,
  ): void {
    const allValues = items.map((item) => item.value);
    const shouldSelectAll = formControl.value?.length !== allValues.length;

    items.forEach((item) => {
      item.selected = shouldSelectAll;
    });
    formControl.setValue(shouldSelectAll ? allValues : []);
    this.markFilterAsCustom();
  }
}
